import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JourneyContext = createContext(null);

export const JourneyProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [journeyState, setJourneyState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('anonymous_session_id'));

  // Create anonymous session for discovery (Stage 0)
  const createAnonymousSession = useCallback(async (deviceInfo = {}, referralSource = null) => {
    try {
      const response = await axios.post(`${API}/journey/anonymous/session`, {
        device_info: deviceInfo,
        referral_source: referralSource,
        inferred_geo: { country: Intl.DateTimeFormat().resolvedOptions().timeZone.includes('Asia') ? 'India' : 'Unknown' }
      });
      
      const { session_id } = response.data;
      setSessionId(session_id);
      localStorage.setItem('anonymous_session_id', session_id);
      return session_id;
    } catch (error) {
      console.error('Failed to create anonymous session:', error);
      return null;
    }
  }, []);

  // Tag user intent (Stage 0)
  const tagIntent = useCallback(async (intents) => {
    if (!sessionId) return null;
    
    try {
      const response = await axios.post(`${API}/journey/anonymous/${sessionId}/intent`, {
        intents
      });
      return response.data;
    } catch (error) {
      console.error('Failed to tag intent:', error);
      return null;
    }
  }, [sessionId]);

  // Track engagement during first launch (Stage 1)
  const trackEngagement = useCallback(async (action, element = null, durationMs = null, metadata = {}) => {
    if (!sessionId) return null;
    
    try {
      const response = await axios.post(`${API}/journey/anonymous/${sessionId}/engagement`, {
        action,
        element,
        duration_ms: durationMs,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Failed to track engagement:', error);
      return null;
    }
  }, [sessionId]);

  // Notify account created (Stage 2) - called after registration
  const notifyAccountCreated = useCallback(async () => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API}/journey/account-created`,
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear anonymous session after merge
      localStorage.removeItem('anonymous_session_id');
      setSessionId(null);
      
      return response.data;
    } catch (error) {
      console.error('Failed to notify account created:', error);
      return null;
    }
  }, [token, sessionId]);

  // Get journey state for authenticated user
  const fetchJourneyState = useCallback(async () => {
    if (!token) return null;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/journey/state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJourneyState(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch journey state:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Get first personalized insight (Stage 5)
  const getFirstAha = useCallback(async () => {
    if (!token) return null;
    
    try {
      const response = await axios.get(`${API}/journey/first-aha`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get first aha:', error);
      return null;
    }
  }, [token]);

  // Log reaction to insight (Stage 5)
  const logReaction = useCallback(async (reactionType, rating = null, feedback = null) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API}/journey/first-aha/reaction`,
        { reaction_type: reactionType, rating, feedback },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to log reaction:', error);
      return null;
    }
  }, [token]);

  // Track feature access (Stage 6)
  const trackFeatureAccess = useCallback(async (feature, context = {}) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API}/journey/feature-access`,
        { feature, context },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to track feature access:', error);
      return null;
    }
  }, [token]);

  // Get value consumption metrics (Stage 6)
  const getValueConsumption = useCallback(async () => {
    if (!token) return null;
    
    try {
      const response = await axios.get(`${API}/journey/value-consumption`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get value consumption:', error);
      return null;
    }
  }, [token]);

  // Check conversion readiness (Stage 7)
  const checkConversion = useCallback(async () => {
    if (!token) return null;
    
    try {
      const response = await axios.get(`${API}/journey/conversion-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to check conversion:', error);
      return null;
    }
  }, [token]);

  // Select subscription plan (Stage 7)
  const selectPlan = useCallback(async (plan) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API}/journey/conversion/plan-selected`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to select plan:', error);
      return null;
    }
  }, [token]);

  // Check if user can access a feature
  const canAccessFeature = useCallback(async (feature) => {
    if (!token) return { can_access: false, reason: 'Not authenticated' };
    
    try {
      const response = await axios.get(`${API}/journey/can-access/${feature}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to check feature access:', error);
      return { can_access: false, reason: 'Error checking access' };
    }
  }, [token]);

  // Submit feedback (Stage 10)
  const submitFeedback = useCallback(async (feature, accuracyRating, relevanceRating, feedbackText = null) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API}/journey/feedback`,
        {
          feature,
          accuracy_rating: accuracyRating,
          relevance_rating: relevanceRating,
          feedback_text: feedbackText
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return null;
    }
  }, [token]);

  // Fetch journey state when user logs in
  useEffect(() => {
    if (user && token) {
      fetchJourneyState();
    }
  }, [user, token, fetchJourneyState]);

  // Create anonymous session on first visit
  useEffect(() => {
    if (!sessionId && !user) {
      createAnonymousSession({
        platform: 'web',
        browser: navigator.userAgent.includes('Chrome') ? 'chrome' : 'other'
      });
    }
  }, [sessionId, user, createAnonymousSession]);

  const value = {
    // State
    journeyState,
    loading,
    sessionId,
    
    // Stage 0: Discovery
    createAnonymousSession,
    tagIntent,
    
    // Stage 1: First Launch
    trackEngagement,
    
    // Stage 2: Account Creation
    notifyAccountCreated,
    
    // Stage 5: First Aha
    getFirstAha,
    logReaction,
    
    // Stage 6: Free Exploration
    trackFeatureAccess,
    getValueConsumption,
    
    // Stage 7: Conversion
    checkConversion,
    selectPlan,
    
    // Stage 10: Retention
    submitFeedback,
    
    // Utilities
    fetchJourneyState,
    canAccessFeature
  };

  return (
    <JourneyContext.Provider value={value}>
      {children}
    </JourneyContext.Provider>
  );
};

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
};

export default JourneyContext;
